import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

const LOGIN_BG = "https://images.unsplash.com/photo-1601285129072-1642f929304a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3Nzc1NjEyNDl8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [loginId, setLoginId] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (user) return <Navigate to="/app/empresas" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(loginId, password);
      toast.success("Autenticado en Odoo");
      navigate("/app/empresas");
    } catch (err) {
      const msg = err?.response?.data?.detail || "No se pudo iniciar sesión";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#F8FAFC]" data-testid="login-page">
      <div className="hidden lg:flex relative bg-[#0F172A] overflow-hidden">
        <img
          src={LOGIN_BG}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-80"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#002FA7]/60 via-[#0F172A]/70 to-[#0F172A]/95" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full text-white">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded bg-white text-[#002FA7] flex items-center justify-center font-bold">
              BI
            </div>
            <span className="font-semibold tracking-tight">Odoo Business Intelligence</span>
          </div>
          <div>
            <h1 className="text-4xl lg:text-5xl font-semibold tracking-tight leading-tight">
              Datos de tu ERP,<br />
              <span className="text-[#93B4FF]">decisiones en minutos.</span>
            </h1>
            <p className="mt-4 text-sm text-slate-300 max-w-md">
              Dashboard ejecutivo sobre Odoo 16 CE. Proyectos, contabilidad, analítica,
              compras, ventas y partes de horas en una sola vista.
            </p>
          </div>
          <div className="text-xs text-slate-400">
            © {new Date().getFullYear()} Grupo Cuatroplus · Entorno de desarrollo
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-sm">
          <div className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 tracking-tight">Iniciar sesión</h2>
            <p className="text-sm text-slate-500 mt-1">
              Usa tus credenciales de Odoo para acceder
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="login" className="text-xs tracking-wide uppercase font-semibold text-slate-600">
                Usuario
              </Label>
              <Input
                id="login"
                type="text"
                value={loginId}
                onChange={(e) => setLoginId(e.target.value)}
                placeholder="usuario@cua.com"
                autoComplete="username"
                required
                data-testid="login-input"
                className="h-10"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs tracking-wide uppercase font-semibold text-slate-600">
                Contraseña
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                data-testid="password-input"
                className="h-10"
              />
            </div>

            {error && (
              <div
                className="flex items-start gap-2 text-xs text-red-600 bg-red-50 border border-red-100 p-2.5 rounded"
                data-testid="login-error"
              >
                <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-10 bg-[#002FA7] hover:bg-[#002280] font-medium"
              data-testid="login-submit-button"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {loading ? "Conectando..." : "Entrar"}
            </Button>

            <p className="text-[11px] text-slate-400 pt-2 leading-relaxed">
              Tus credenciales se usan para autenticar en <span className="font-medium text-slate-600">
              gestion-dev-do.grupocuatroplus.com</span> y nunca se almacenan en texto plano.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
